import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, PanelRightOpen } from 'lucide-react';
import CodePanel from '@stepflow/ui-shared/components/CodePanel.jsx';
import { codeLangOf } from '@stepflow/ui-shared/utils/codeLang.js';
import { roomFileUrl, formatBytes } from '../../utils/roomFileUrls.js';
import { useSeen } from './FileCard.jsx';

/**
 * CODE FILE PANEL (CONTEXT-DOCS.md §P4) — a shared code file (.py .json .yaml … — a `</>` snippet or a 📎 file) shows
 * IN the stream as a shaded code panel, not a file card. LIGHT: nothing loads until the panel scrolls into view, then
 * only the TOP of the file (``PREVIEW_BYTES``, an HTTP range read); "Show the rest" loads the whole file on demand.
 */
export const PREVIEW_BYTES = 4096;

/** The top of a file → its complete lines (a cut-off last line / half a character is dropped). Pure. */
export function topLines(text = '', cut = false) {
  const s = String(text || '');
  if (!cut) return s;
  const i = s.lastIndexOf('\n');
  return i > 0 ? s.slice(0, i) : s;
}

/** GET the file: ``range`` → just the first PREVIEW_BYTES. → {text, partial}. A server that ignores the range (200)
 *  sends the whole file — then it simply IS whole. */
export async function readFileText(url, { range = false, size = 0, fetchImpl = fetch } = {}) {
  let r;
  try {
    r = await fetchImpl(url, range ? { headers: { Range: `bytes=0-${PREVIEW_BYTES - 1}` } } : undefined);
  } catch (e) {
    if (!range) throw e;
    r = await fetchImpl(url);          // a store / proxy that refuses ranges → the whole file, never an empty panel
  }
  if (!r.ok) throw new Error("Couldn't load the file.");
  const text = await r.text();
  const partial = r.status === 206 && (size ? Number(size) > PREVIEW_BYTES : text.length >= PREVIEW_BYTES);
  return { text: topLines(text, partial), partial };
}

export default function CodeFilePanel({ networkId, poolId, message, att, onOpen = null }) {
  const ref = useRef(null);
  const seen = useSeen(ref);
  const lang = codeLangOf(att.name) || { grammar: '', label: '' };
  const [state, setState] = useState({ text: '', partial: false, error: '', loaded: false });
  useEffect(() => { setState({ text: '', partial: false, error: '', loaded: false }); }, [message.id, att.index, att.name]);

  useEffect(() => {
    if (!seen || !networkId || state.loaded) return undefined;
    let gone = false;
    roomFileUrl(networkId, poolId, message, att.index)
      .then((u) => readFileText(u.url, { range: true, size: att.size }))
      .then((d) => { if (!gone) setState({ ...d, error: '', loaded: true }); })
      .catch((e) => { if (!gone) setState({ text: '', partial: false, error: (e && e.message) || "Couldn't load the file.", loaded: true }); });
    return () => { gone = true; };
  }, [seen, networkId, poolId, message, att.index, att.size, state.loaded]);

  const loadAll = useCallback(async () => {
    if (!state.partial) return state.text;
    const u = await roomFileUrl(networkId, poolId, message, att.index);
    const d = await readFileText(u.url, { range: false });
    setState({ text: d.text, partial: false, error: '', loaded: true });
    return d.text;
  }, [state.partial, state.text, networkId, poolId, message, att.index]);

  const download = async () => {
    try {
      const u = await roomFileUrl(networkId, poolId, message, att.index);
      const a = document.createElement('a');
      a.href = u.url; a.download = att.name; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
    } catch { /* the Doc pane is the fallback */ }
  };

  const lines = state.text ? state.text.split('\n').length : 0;
  const meta = [lang.label, !state.partial && lines ? `${lines} line${lines === 1 ? '' : 's'}` : '', formatBytes(att.size)]
    .filter(Boolean).join(' · ');
  const open = typeof onOpen === 'function' ? () => onOpen(message, att) : null;
  const iconBtn = 'inline-flex h-6 w-6 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-900';
  return (
    <div ref={ref} className="w-full" data-testid={`code-file-${message.id}-${att.index}`} data-kind="code">
      <CodePanel testId={`code-file-panel-${message.id}-${att.index}`} code={state.text} grammar={lang.grammar}
        title={att.name} meta={meta} truncated={state.partial} onExpand={loadAll} getCopyText={loadAll} placeholder={state.loaded ? '' : 'Loading…'}
        moreLabel={`Show the rest${att.size ? ` (${formatBytes(att.size)})` : ''}`} onTitleClick={open}
        actions={(<>
          <button type="button" onClick={download} aria-label="Download" title="Download" className={iconBtn}
            data-testid={`code-file-download-${message.id}-${att.index}`}><Download className="h-3.5 w-3.5" aria-hidden /></button>
          {open ? (
            <button type="button" onClick={open} aria-label="Open beside the room" title="Open beside the room" className={iconBtn}
              data-testid={`code-file-open-${message.id}-${att.index}`}><PanelRightOpen className="h-3.5 w-3.5" aria-hidden /></button>
          ) : null}
        </>)} />
      {!state.loaded ? <div className="sr-only">Loading {att.name}</div> : null}
      {state.error ? <div data-testid={`code-file-error-${message.id}-${att.index}`} className="text-[0.75rem] text-rose-600">{state.error}</div> : null}
    </div>
  );
}
